"use client";

import Image from "next/image";
import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { Check, Minus, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { redirectToBilling } from "@/lib/intentional-navigation";
import { resolvePurchaseReturnPath } from "@/lib/billing/purchase-return-path";
import { isCustomCheckoutEnabled, customCheckoutHref } from "@/lib/billing/checkout-mode";
import { interceptIfPaymentsPaused } from "@/components/billing/payments-paused-store";
import {
  PLANS,
  PAID_PLAN_IDS,
  COMPARISON_SECTIONS,
  TRY_FREE_FEATURES,
  CONTACT_SALES_MAILTO,
  type ComparisonRow,
  type PaidPlanId,
  type Cadence,
} from "@/lib/plans";
import {
  trackCheckoutInitiated,
  trackCheckoutStarted,
  trackPricingCtaClicked,
  trackPlanCtaClicked,
  trackPlanSelected,
  trackBillingToggleClicked,
  trackPricingToggleSwitched,
  trackPricingCtaError,
} from "@/lib/analytics";
import { toast } from "sonner";
import { useResetOnBfcache } from "@/lib/use-reset-on-bfcache";
import { useBillingCadence } from "@/lib/billing/cadence-preference";
import { completedOnSavedCard } from "@/lib/billing/direct-charge-client";

interface PricingSectionProps {
  user?: { email?: string } | null;
  /** Opens the login/signup modal when an anonymous visitor selects a paid
   *  plan (they must authenticate before checkout). Parent owns the modal. */
  openLogin: (nextPath: string) => void;
  /** Where to send the user after they sign in / sign up. */
  postAuthPath?: string;
  /** Show the "Try Panda free" promo card. On the standalone /pricing
   *  page but not the homepage section. */
  showTryFreeCard?: boolean;
  /** Optional CTA for the free-trial card — invoked when a signed-in free
   *  user with no project clicks the bottom CTA (routes them to the build
   *  prompt). Defaults to pushing the homepage prompt box. */
  onStartForFree?: () => void;
  /** Anchor id — used so existing `#pricing` deep links still scroll
   *  to this section. */
  anchorId?: string;
  /** The signed-in user's active plan ("free" | "starter" | "pro" |
   *  "scale"). The matching card's button is disabled and relabelled
   *  "Current plan". */
  currentPlan?: string;
  /**
   * Has that plan been read from the server yet? Same reasoning as the prop of
   * the same name on PricingTiersSection: `currentPlan` lands a beat after the
   * page paints, and until it does `isCurrent()` answers false for every card,
   * so an active subscriber gets a live CTA on the plan they already hold.
   * Defaults true so existing callers are unchanged.
   */
  planResolved?: boolean;
  /** Whether the signed-in user has already created at least one project.
   *  Drives the bottom CTA copy/route ("Start building" vs "Go to my
   *  projects") so a returning builder never sees a logged-out CTA. */
  hasProject?: boolean;
}

export function PricingSection({
  user,
  openLogin,
  postAuthPath = "/pricing",
  showTryFreeCard = false,
  onStartForFree,
  anchorId,
  currentPlan,
  planResolved = true,
  hasProject = false,
}: PricingSectionProps) {
  const router = useRouter();
  // Shared with the in-app plan screens and persisted, so a visitor who picks
  // Annual here still sees Annual in the upgrade modal (and on their next
  // visit). Opens on annual — the product default — for a first-time visitor.
  const [billing, setBilling] = useBillingCadence();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  // Browser back from Stripe Checkout restores this section from the
  // back-forward cache with `checkoutLoading` still set to the plan being
  // purchased, leaving that plan's CTA stuck on its spinner (PANDA-1461). Clear
  // it on a bfcache restore so the pricing buttons are interactive again.
  useResetOnBfcache(useCallback(() => setCheckoutLoading(null), []));
  const [selectedMobileIdx, setSelectedMobileIdx] = useState(0);

  // Shared analytics context attached to every pricing CTA event.
  const isLoggedIn = !!user;
  const normalizedPlan = (currentPlan ?? "free").toLowerCase();

  // The free-tier CTA ("Get started for free" logged-out / "Start building"
  // signed-in-free). The page passes `onStartForFree`, which routes through the
  // shared start-free helper: signed-out visitors open signup (never a dead-end
  // prompt refocus, PANDA-1009), signed-in visitors go into the app.
  //
  // The `?focus=prompt` branch is a documented fallback ONLY for a caller that
  // mounts this section without `onStartForFree`; it scrolls the homepage prompt
  // box in and focuses it. No shipped surface hits it — every mount supplies the
  // handler — so a "Start free" click never merely refocuses the prompt in prod.
  function goToBuild() {
    if (onStartForFree) onStartForFree();
    else router.push("/?focus=prompt");
  }

  function switchBilling(next: Cadence) {
    if (next === billing) return;
    const from = billing;
    setBilling(next);
    // Both fire: the older event feeds existing dashboards, the new one carries
    // the surface + from_cadence the annual-mix reading needs.
    trackBillingToggleClicked({
      billing: next,
      is_logged_in: isLoggedIn,
      current_plan: normalizedPlan,
    });
    trackPricingToggleSwitched({
      surface: "pricing_page",
      from_cadence: from,
      to_cadence: next,
    });
  }

  async function handleCheckout(plan: PaidPlanId) {
    const label = ctaLabel(plan);
    trackPricingCtaClicked({
      is_logged_in: isLoggedIn,
      current_plan: normalizedPlan,
      selected_plan: plan,
      cta_label: label,
    });
    // Fired before the auth gate below, so a signed-out click still records the
    // plan + cadence the visitor wanted.
    trackPlanCtaClicked({
      surface: "pricing_page",
      plan,
      cadence: billing,
    });
    if (!user) {
      // Anonymous → open the login/signup modal so they can authenticate
      // before checkout (existing behaviour for the paid plan cards).
      openLogin(postAuthPath);
      return;
    }
    trackPlanSelected({
      is_logged_in: true,
      current_plan: normalizedPlan,
      selected_plan: plan,
      cta_label: label,
    });
    setCheckoutLoading(plan);
    // Before the request, so it covers BOTH outcomes: a Stripe redirect and a
    // saved-card charge that never reaches Stripe (and so never fires
    // checkout_initiated below).
    trackCheckoutStarted({ plan, cadence: billing, surface: "pricing_page" });
    // Payments-paused guard. After the click analytics above so a blocked
    // purchase still registers as intent everywhere it did before.
    if (
      interceptIfPaymentsPaused({
        kind: "upgrade",
        surface: "pricing_section",
        label: PLANS[plan].name,
        priceLabel:
          billing === "annual"
            ? `$${(PLANS[plan].annual.priceUsd / 12).toFixed(0)}/mo billed yearly`
            : `$${PLANS[plan].monthly.priceUsd}/mo`,
        plan,
        cadence: billing,
        amountUsd:
          billing === "annual"
            ? PLANS[plan].annual.priceUsd
            : PLANS[plan].monthly.priceUsd,
      })
    ) {
      setCheckoutLoading(null);
      return;
    }
    // Where this buyer came from — the page before /pricing, or /projects when
    // they arrived cold. Resolved at click time, and used by every branch below
    // so the hosted redirect, the custom checkout and the saved-card fast path
    // cannot land in three different places.
    const returnTo = resolvePurchaseReturnPath();
    // Custom Payment Element checkout (behind the flag): go to /checkout. The
    // saved-card fast path still runs there (charged server-side before Elements).
    if (isCustomCheckoutEnabled()) {
      redirectToBilling(
        customCheckoutHref({ type: "plan", plan, cadence: billing, returnTo }),
      );
      return;
    }
    try {
      const res = await fetch("/api/credits/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, cadence: billing, returnTo }),
      });
      const data = await res.json().catch(() => null);
      // Card on file → already charged and activated server-side; land where the
      // user was, so the new plan is live on the page they left.
      if (res.ok && completedOnSavedCard(data, returnTo)) return;
      if (res.ok && data?.url) {
        // INTENT: subscription Checkout URL received, about to redirect.
        trackCheckoutInitiated(plan, "pricing_page");
        redirectToBilling(data.url);
        return;
      }
      // Checkout failed (error status, or a 200 with no URL). Previously the
      // button just snapped back to its default label with no explanation —
      // the "silent redirection failure" users reported (PANDA-1047). Surface
      // the server's reason so the click never dead-ends quietly.
      const message = data?.error || "Couldn't start checkout. Please try again.";
      toast.error(message);
      trackPricingCtaError({
        is_logged_in: true,
        current_plan: normalizedPlan,
        selected_plan: plan,
        cta_label: label,
        message,
      });
      setCheckoutLoading(null);
    } catch {
      const message =
        "Couldn't reach checkout. Please check your connection and try again.";
      toast.error(message);
      trackPricingCtaError({
        is_logged_in: true,
        current_plan: normalizedPlan,
        selected_plan: plan,
        cta_label: label,
        message,
      });
      setCheckoutLoading(null);
    }
  }

  // emerald-700 / emerald tint since PANDA-2025, matching PricingTiersSection.
  // This is the pre-NEW_PRICING_PAGE variant: still reachable behind the flag,
  // so leaving it orange would make flipping the flag a colour regression.
  const accent = "#047857";
  const accentBgClass = "bg-emerald-50 dark:bg-emerald-500/[0.08]";

  const cards = PAID_PLAN_IDS.map((id) => {
    const def = PLANS[id];
    const price = billing === "annual" ? def.annual.priceUsd : def.monthly.priceUsd;
    const yearlyTotal = billing === "annual" ? def.annual.priceUsd : null;
    return {
      id,
      name: def.name,
      tagline: def.tagline,
      // Show effective monthly rate for annual (price / 12); show actual price for monthly.
      displayPrice:
        billing === "annual"
          ? `$${(def.annual.priceUsd / 12).toFixed(0)}`
          : `$${def.monthly.priceUsd}`,
      yearlyTotal,
      highlighted: def.highlighted,
      bullets: planBullets(id),
    };
  });

  function isCurrent(planId: string) {
    // Unresolved is not "not current" — see the prop's doc.
    return (
      planResolved &&
      !!currentPlan &&
      currentPlan.toLowerCase() === planId.toLowerCase()
    );
  }

  // CTA copy reflects the visitor's session + subscription state so an
  // active paying customer never sees the anonymous "Get started for free"
  // marketing copy (PANDA-1009). Anonymous visitors keep the original copy;
  // signed-in users see their plan position relative to each card.
  function ctaLabel(planId: string) {
    if (!user) return "Get started for free";
    if (isCurrent(planId)) return "Current plan";
    const current = PLAN_RANK[(currentPlan ?? "free").toLowerCase()] ?? 0;
    const target = PLAN_RANK[planId.toLowerCase()] ?? 0;
    return target > current ? "Upgrade" : "Downgrade";
  }

  // True when the visitor is signed in on any paid tier.
  const isPaidUser =
    !!user && (PLAN_RANK[(currentPlan ?? "free").toLowerCase()] ?? 0) > 0;

  // Bottom "Try Panda for free" card. Paid users never see it (render guard
  // below). Copy + route adapt to auth + project state so a signed-in user is
  // always routed toward building, never to a logged-out CTA (PANDA-1009):
  //   • logged-out            → signup
  //   • free, no project yet  → build prompt ("Start building")
  //   • free, has a project   → projects dashboard ("Go to my projects")
  function tryFreeCta() {
    function fire(label: string) {
      trackPricingCtaClicked({
        is_logged_in: isLoggedIn,
        current_plan: normalizedPlan,
        selected_plan: null,
        cta_label: label,
      });
    }
    if (!user) {
      const label = "Get started for free";
      return {
        heading: "Try Panda for free",
        subtext: "No credit card required. Upgrade any time.",
        label,
        onClick: () => {
          fire(label);
          goToBuild();
        },
      };
    }
    if (hasProject) {
      const label = "Go to my projects";
      return {
        heading: "Pick up where you left off",
        subtext: "You're on the Free plan. Jump back into your projects.",
        label,
        onClick: () => {
          fire(label);
          router.push("/projects");
        },
      };
    }
    const label = "Start building";
    return {
      heading: "Create your first project",
      subtext:
        "You're on the Free plan — start building, no credit card required.",
      label,
      onClick: () => {
        fire(label);
        goToBuild();
      },
    };
  }
  const tryFree = showTryFreeCard && !isPaidUser ? tryFreeCta() : null;

  return (
    <section
      id={anchorId}
      className="relative bg-background py-16 dark:bg-background sm:py-24"
    >
      <div className="mx-auto max-w-[1264px] px-4 sm:px-8">
        {/* Heading */}
        <div className="flex max-w-[648px] flex-col gap-2">
          <h2 className="font-[family-name:var(--font-rubik),sans-serif] text-[36px] font-medium leading-[44px] text-foreground sm:text-[48px] sm:leading-[56px]">
            The{" "}
            <span className="bg-panda-gradient-success-text bg-clip-text text-transparent">
              right plan
            </span>{" "}
            for your business
          </h2>
          <p className="font-[family-name:var(--font-rubik),sans-serif] text-base font-normal leading-6 text-[#475569] dark:text-muted-foreground">
            Build apps, launch campaigns, and grow customers — all from one
            subscription.
          </p>
        </div>

        {/* Billing toggle */}
        <div className="mt-8 flex items-center justify-center gap-4">
          <div className="relative inline-flex items-center">
            <span
              aria-hidden
              className="pointer-events-none absolute -top-7 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-full bg-[#12B76A] px-3 py-1 text-xs font-medium text-white lg:block"
              style={{ fontFamily: "var(--font-rubik), sans-serif" }}
            >
              2 MONTHS FREE WITH YEARLY
            </span>
            <div className="inline-flex h-10 items-center rounded-full bg-[#F1F5F9] p-1 dark:bg-[#18212F]">
              <button
                type="button"
                onClick={() => switchBilling("monthly")}
                aria-pressed={billing === "monthly"}
                className={cn(
                  "inline-flex h-full items-center justify-center rounded-full px-4 py-2.5 font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium leading-5 transition-all",
                  billing === "monthly"
                    ? "bg-white text-[#1A1A1B] shadow-sm dark:bg-[#080b12] dark:text-[#f1f5f9]"
                    : "text-foreground/70 hover:text-foreground",
                )}
              >
                Bill monthly
              </button>
              <button
                type="button"
                onClick={() => switchBilling("annual")}
                aria-pressed={billing === "annual"}
                className={cn(
                  "inline-flex h-full items-center justify-center rounded-full px-4 py-2.5 font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium leading-5 transition-all",
                  billing === "annual"
                    ? "bg-white text-[#1A1A1B] shadow-sm dark:bg-[#080b12] dark:text-[#f1f5f9]"
                    : "text-foreground/70 hover:text-foreground",
                )}
              >
                Bill yearly
              </button>
            </div>
          </div>
        </div>

        {/* Desktop: 3 cards side by side */}
        <div className="mt-14 hidden grid-cols-3 gap-6 lg:grid">
          {cards.map((c) => (
            <PlanCard
              key={c.id}
              card={c}
              billing={billing}
              accent={accent}
              accentBgClass={accentBgClass}
              isCurrent={isCurrent(c.id)}
              awaitingPlan={!planResolved}
              ctaLabel={ctaLabel(c.id)}
              isLoading={checkoutLoading === c.id}
              onClick={() => handleCheckout(c.id)}
            />
          ))}
        </div>

        {/* Mobile: stacked cards */}
        <div className="mt-10 flex flex-col gap-6 lg:hidden">
          {cards.map((c, idx) => (
            <div
              key={c.id}
              role="button"
              tabIndex={0}
              aria-pressed={idx === selectedMobileIdx}
              onClick={() => setSelectedMobileIdx(idx)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelectedMobileIdx(idx);
                }
              }}
              className={cn(
                "cursor-pointer outline-none",
                idx === selectedMobileIdx &&
                  "rounded-2xl ring-2 ring-foreground ring-offset-2 ring-offset-background",
              )}
            >
              <PlanCard
                card={c}
                billing={billing}
                accent={accent}
                accentBgClass={accentBgClass}
                isCurrent={isCurrent(c.id)}
                awaitingPlan={!planResolved}
                ctaLabel={ctaLabel(c.id)}
                isLoading={checkoutLoading === c.id}
                onClick={() => handleCheckout(c.id)}
              />
            </div>
          ))}
        </div>

        {/* Comparison table */}
        <div className="mt-20">
          <h3 className="font-[family-name:var(--font-rubik),sans-serif] text-[36px] font-medium leading-[44px] text-foreground sm:text-[48px] sm:leading-[56px]">
            Compare all features
          </h3>

          {/* Desktop table */}
          <div className="mt-10 hidden lg:block">
            <div className="grid grid-cols-[1fr_repeat(3,160px)] items-center gap-4 border-b border-[#E2E8F0] py-4 dark:border-border">
              <span className="font-[family-name:var(--font-rubik),sans-serif] text-base font-medium text-foreground">
                Features
              </span>
              {cards.map((c) => (
                <span
                  key={c.id}
                  className="text-center font-[family-name:var(--font-rubik),sans-serif] text-base font-medium text-foreground"
                >
                  {c.name}
                </span>
              ))}
            </div>
            {COMPARISON_SECTIONS.map((section) => (
              <div key={section.heading}>
                <div className="mt-6 flex items-center gap-2 py-2">
                  <span className="font-[family-name:var(--font-rubik),sans-serif] text-base font-medium text-foreground">
                    {section.heading}
                  </span>
                </div>
                {section.rows.map((row) => (
                  <div
                    key={row.label}
                    className="grid grid-cols-[1fr_repeat(3,160px)] items-center gap-4 border-b border-[#E2E8F0] py-3 dark:border-border"
                  >
                    <div className="font-[family-name:var(--font-rubik),sans-serif] text-sm text-foreground">
                      {row.label}
                    </div>
                    {row.values.map((v, i) => (
                      <div key={i} className="flex justify-center">
                        <ComparisonCell value={v} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Mobile table */}
          <div className="mt-6 lg:hidden">
            <div className="sticky top-16 z-30 -mx-4 flex items-center gap-4 bg-background px-4 py-1.5 shadow-[0px_3px_7.5px_rgba(189,189,189,0.2)] sm:-mx-8 sm:top-18 sm:px-8 dark:border-b dark:border-border dark:bg-background dark:shadow-none">
              <span className="shrink-0 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium text-foreground">
                Plan:
              </span>
              <div className="flex items-center gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {cards.map((c, idx) => (
                  <button
                    key={c.id}
                    type="button"
                    aria-pressed={idx === selectedMobileIdx}
                    onClick={() => setSelectedMobileIdx(idx)}
                    className={cn(
                      "shrink-0 rounded-full px-4 py-1.5 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 transition-colors",
                      idx === selectedMobileIdx
                        ? "bg-[#1A1A1B] text-white dark:bg-foreground dark:text-background"
                        : "border border-[#CBD5E1] text-[#1A1A1B] hover:bg-[#F1F5F9] dark:border-border dark:text-foreground dark:hover:bg-card",
                    )}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
            {COMPARISON_SECTIONS.map((section) => (
              <div key={section.heading}>
                <div className="mt-6 py-2">
                  <span className="font-[family-name:var(--font-rubik),sans-serif] text-base font-medium text-foreground">
                    {section.heading}
                  </span>
                </div>
                {section.rows.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-center gap-6 border-b border-[#E2E8F0] dark:border-border"
                  >
                    <div className="flex-1 py-3 font-[family-name:var(--font-rubik),sans-serif] text-sm text-foreground">
                      {row.label}
                    </div>
                    <div className="flex w-30 shrink-0 items-center justify-center py-2.5">
                      <ComparisonCell value={row.values[selectedMobileIdx]} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Optional "Try Panda free" card on the dedicated /pricing page.
            Hidden for users already on a paid plan — a "No credit card
            required" promo is just as jarring for them as the CTA was
            (PANDA-1009). Copy + route adapt to auth + project state via
            tryFreeCta() so signed-in users are routed toward building. */}
        {tryFree && (
          <div className="mt-20 rounded-2xl border border-[#E2E8F0] bg-card p-8 dark:border-border">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="font-[family-name:var(--font-rubik),sans-serif] text-2xl font-medium text-foreground">
                  {tryFree.heading}
                </h3>
                <p className="mt-1 text-sm text-[#475569] dark:text-muted-foreground">
                  {tryFree.subtext}
                </p>
                <ul className="mt-3 flex flex-col gap-1 text-sm text-foreground">
                  {TRY_FREE_FEATURES.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-[#12B76A]" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                onClick={tryFree.onClick}
                className="inline-flex h-12 items-center justify-center rounded-full bg-[#080B12] px-6 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium text-white dark:bg-foreground dark:text-background"
              >
                {tryFree.label}
              </button>
            </div>
          </div>
        )}

        {/* Enterprise contact link */}
        <div className="mt-12 text-center">
          <a
            href={CONTACT_SALES_MAILTO}
            className="text-sm text-[#475569] underline-offset-4 hover:underline dark:text-muted-foreground"
          >
            Need higher volumes, SSO, or onboarding support? Contact sales.
          </a>
        </div>
      </div>
    </section>
  );
}

// Plan tiers ranked low→high so the CTA can tell upgrade from downgrade
// relative to the signed-in user's current plan.
const PLAN_RANK: Record<string, number> = {
  free: 0,
  starter: 1,
  pro: 2,
  scale: 3,
};

// Per-card bullet list pulled from the PLANS record. Mirrors the screenshot:
// build credits / projects / grow actions and a couple of feature highlights.
function planBullets(id: PaidPlanId): string[] {
  const def = PLANS[id];
  const lines: string[] = [
    `${def.buildCreditsPerMonth} credits/mo`,
    `${def.projectsLimit} projects`,
    "Autonomous Grow Mode",
  ];
  if (id === "starter") {
    lines.push("All marketing channels", "Hosting + premium templates");
  } else if (id === "pro") {
    lines.push("Custom domain + payments", "Remove Panda branding");
  } else {
    lines.push("Everything in Pro + priority support", "50 posts/mo");
  }
  return lines;
}

interface CardData {
  id: PaidPlanId;
  name: string;
  tagline: string;
  displayPrice: string;
  yearlyTotal: number | null;
  highlighted: boolean;
  bullets: string[];
}

function PlanCard({
  card,
  billing,
  accent,
  accentBgClass,
  isCurrent,
  awaitingPlan = false,
  ctaLabel,
  isLoading,
  onClick,
}: {
  card: CardData;
  billing: Cadence;
  accent: string;
  accentBgClass: string;
  isCurrent: boolean;
  /** The account's plan has not been read yet, so this CTA cannot know whether
   *  it would be a purchase or a duplicate. Held rather than guessed. */
  awaitingPlan?: boolean;
  ctaLabel: string;
  isLoading: boolean;
  onClick: () => void;
}) {
  return (
    <div
      className={cn(
        "relative flex flex-col overflow-hidden rounded-2xl border bg-card",
        card.highlighted ? accentBgClass : "border-border",
      )}
      style={card.highlighted ? { borderColor: accent } : undefined}
    >
      {card.highlighted && (
        <div
          className="flex h-7 items-center justify-center font-[family-name:var(--font-rubik),sans-serif] text-xs font-medium uppercase leading-4 text-white"
          style={{ background: accent }}
        >
          Most popular
        </div>
      )}
      <div className="flex flex-col gap-4 p-6">
        <div className="flex flex-col gap-1">
          <p className="font-[family-name:var(--font-rubik),sans-serif] text-2xl font-medium leading-8 text-foreground">
            {card.name}
          </p>
          <p className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-muted-foreground">
            {card.tagline}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-baseline">
            <span className="font-[family-name:var(--font-rubik),sans-serif] text-[40px] font-medium leading-12 text-foreground">
              {card.displayPrice}
            </span>
            <span className="ml-1 font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-muted-foreground">
              /month
            </span>
          </div>
          {card.yearlyTotal !== null && (
            <p className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-muted-foreground">
              ${card.yearlyTotal.toLocaleString()}/year billed annually
            </p>
          )}
          {billing === "monthly" && (
            <p className="font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 text-[#77869D] dark:text-muted-foreground">
              ${PLANS[card.id].annual.priceUsd.toLocaleString()}/year if billed annually
            </p>
          )}
        </div>
        <button
          type="button"
          disabled={isLoading || isCurrent || awaitingPlan}
          aria-current={isCurrent ? "true" : undefined}
          aria-busy={awaitingPlan ? "true" : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className={cn(
            "inline-flex h-12 w-full items-center justify-center gap-2 rounded-full px-6 py-3 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-white transition-all disabled:cursor-not-allowed disabled:opacity-60",
            card.highlighted
              ? "hover:brightness-110"
              : "bg-[#080B12] hover:brightness-110 dark:bg-foreground dark:text-background",
          )}
          style={card.highlighted ? { background: accent } : undefined}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Redirecting…
            </>
          ) : (
            ctaLabel
          )}
        </button>
        <ul className="flex flex-col gap-2">
          {card.bullets.map((b) => (
            <li
              key={b}
              className="flex items-start gap-2 font-[family-name:var(--font-rubik),sans-serif] text-sm leading-5 text-foreground"
            >
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              {b}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ComparisonCell({ value }: { value: ComparisonRow["values"][number] }) {
  if (typeof value === "boolean") {
    return value ? (
      <Check className="h-5 w-5 text-foreground" />
    ) : (
      <Minus className="h-5 w-5 text-[#CBD5E1] dark:text-muted-foreground" />
    );
  }
  return (
    <span className="text-center font-[family-name:var(--font-rubik),sans-serif] text-sm leading-5 text-foreground">
      {value}
    </span>
  );
}

// Image kept for back-compat with parent layouts that pass <Image /> nearby.
void Image;
